import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { z } from "zod";
import { admin, brandsQuery, policiesQuery, qk, usersQuery } from "../../../api/admin";
import { errorMessage } from "../../../api/client";
import type { PolicyDocument, PolicyLayer, PolicyScope } from "../../../api/types";
import { requireAdmin } from "../../../auth/useMe";
import { PolicyForm } from "../../../components/PolicyForm";
import { toast } from "../../../components/toast";
import { Badge, Card, ErrorState, Loading, PageHeader, Segmented, Select } from "../../../components/ui";
import { useNavigate } from "../../../lib/navigate";
import { describeValue, POLICY_KEYS, POLICY_LABELS, resolvePolicy } from "../../../lib/policy";

const searchSchema = z.object({
  scope: z.enum(["tenant", "brand", "user"]).optional().catch(undefined),
  id: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/admin/calling/policies")({
  staticData: { crumb: { label: "Policies", parents: [] } },
  validateSearch: (s) => searchSchema.parse(s),
  beforeLoad: requireAdmin,
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.prefetchQuery(policiesQuery),
      context.queryClient.prefetchQuery(brandsQuery),
      context.queryClient.prefetchQuery(usersQuery),
    ]),
  component: PoliciesPage,
});

const SOURCE_LABEL = {
  tenant: "Company",
  brand: "Brand",
  user: "User",
  default: "Default",
} as const;

function PoliciesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const qc = useQueryClient();
  const policies = useQuery(policiesQuery);
  const brands = useQuery(brandsQuery);
  const users = useQuery(usersQuery);
  const scope: PolicyScope = search.scope ?? "tenant";
  const options =
    scope === "brand"
      ? (brands.data ?? []).map((b) => ({ id: b.id, name: b.name }))
      : (users.data ?? []).map((u) => ({ id: u.id, name: u.name }));
  const scopeId = scope === "tenant" ? null : (search.id ?? options[0]?.id ?? null);
  const user = scope === "user" ? users.data?.find((u) => u.id === scopeId) : undefined;
  const target = useMemo(
    () => ({
      brandId: scope === "brand" ? scopeId : (user?.brand_id ?? null),
      userId: scope === "user" ? scopeId : null,
    }),
    [scope, scopeId, user],
  );

  if (policies.isPending) return <Loading />;
  if (policies.isError) return <ErrorState error={policies.error} onRetry={() => policies.refetch()} />;
  const doc = policies.data;
  const layer: PolicyLayer | undefined =
    scope === "tenant"
      ? doc.tenant
      : scope === "brand"
        ? doc.brands.find((b) => b.brand_id === scopeId)
        : doc.users.find((u) => u.user_id === scopeId);
  const inherited = resolvePolicy(doc, target, scope);
  const effective = resolvePolicy(doc, target);

  const save = async (next: PolicyLayer) => {
    const prev = qc.getQueryData<PolicyDocument>(qk.policies);
    // Optimistic: apply locally, roll back on failure.
    if (prev) qc.setQueryData(qk.policies, applyLayer(prev, scope, scopeId, next));
    try {
      const saved = await admin.putPolicy({
        scope,
        scope_id: scopeId,
        ...next,
      });
      qc.setQueryData(qk.policies, saved);
      qc.invalidateQueries({ queryKey: qk.me });
      toast("Policy saved. Connected apps pick it up right away.");
    } catch (e) {
      if (prev) qc.setQueryData(qk.policies, prev);
      toast(errorMessage(e), "danger");
      throw e;
    }
  };

  return (
    <>
      <PageHeader title="Policies" />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Segmented
          label="Scope"
          value={scope}
          onChange={(s) =>
            navigate({
              search: { scope: s === "tenant" ? undefined : s },
              replace: true,
            })
          }
          options={[
            { value: "tenant", label: "Company" },
            { value: "brand", label: "Brand" },
            { value: "user", label: "User" },
          ]}
        />
        {scope !== "tenant" ? (
          <Select
            aria-label={scope === "brand" ? "Brand" : "User"}
            className="w-56"
            value={scopeId ?? ""}
            onValueChange={(v) =>
              navigate({
                search: (p) => ({ ...p, id: v }),
                replace: true,
              })
            }
            options={[...(options ?? []).map((o) => ({ value: o.id, label: o.name }))]}
          />
        ) : null}
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
        <Card
          title={scope === "tenant" ? "Company policy" : `${scope === "brand" ? "Brand" : "User"} overrides`}
        >
          {scope !== "tenant" && !scopeId ? (
            <p className="px-4 py-6 text-ui text-ink-2">Nothing to pick yet.</p>
          ) : (
            <PolicyForm
              key={`${scope}:${scopeId}:${JSON.stringify(layer ?? null)}`}
              scope={scope}
              layer={layer}
              inherited={inherited}
              onSubmit={save}
            />
          )}
        </Card>
        <Card title="Effective values">
          <dl className="divide-y divide-hairline">
            {POLICY_KEYS.map((k) => (
              <div key={k} className="flex items-start justify-between gap-3 px-4 py-2.5 text-ui">
                <dt className="text-ink-2">{POLICY_LABELS[k]}</dt>
                <dd className="flex flex-col items-end gap-1 text-right">
                  <span>{describeValue(k, effective[k].value)}</span>
                  <span className="flex gap-1">
                    <Badge>{SOURCE_LABEL[effective[k].source]}</Badge>
                    {effective[k].lockedAt ? <Badge tone="warn">Locked</Badge> : null}
                  </span>
                </dd>
              </div>
            ))}
          </dl>
        </Card>
      </div>
    </>
  );
}

function applyLayer(
  doc: PolicyDocument,
  scope: PolicyScope,
  id: string | null,
  layer: PolicyLayer,
): PolicyDocument {
  if (scope === "tenant") return { ...doc, tenant: layer };
  if (scope === "brand")
    return {
      ...doc,
      brands: [...doc.brands.filter((b) => b.brand_id !== id), { brand_id: id!, ...layer }],
    };
  return {
    ...doc,
    users: [...doc.users.filter((u) => u.user_id !== id), { user_id: id!, ...layer }],
  };
}
